import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ArrowLeft, CheckCircle, Clock, XCircle, Search, MoreVertical, MessageSquare } from 'lucide-react';

interface Message {
  id: string;
  recipient_phone: string;
  content: string;
  wa_status: string;
  created_at: string;
  direction: string;
  wa_timestamp: string;
}

interface Instance {
  id: string;
  name: string;
}

interface Conversation {
  phone: string;
  messages: Message[];
  lastMessage: Message;
  unreadCount: number;
}

export default function MessageHistory() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [instance, setInstance] = useState<Instance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (instanceId) {
      fetchInstance();
      const initialFetch = fetchMessages();
      const interval = setInterval(fetchMessages, 5000); // Refresh every 5 seconds

      return () => {
        clearInterval(interval);
      };
    }
  }, [instanceId]);

  useEffect(() => {
    // Group messages by phone number
    const groupedMessages = messages.reduce((acc: { [key: string]: Message[] }, message) => {
      const phone = message.recipient_phone;
      if (!acc[phone]) {
        acc[phone] = [];
      }
      acc[phone].push(message);
      return acc;
    }, {});

    // Convert to conversations array
    const conversationsArray = Object.entries(groupedMessages).map(([phone, messages]) => {
      const sortedMessages = messages.sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      
      return {
        phone,
        messages: sortedMessages,
        lastMessage: sortedMessages[sortedMessages.length - 1],
        unreadCount: messages.filter(m => 
          m.direction === 'incoming' && 
          (!m.wa_status || m.wa_status === 'delivered')
        ).length
      };
    });

    // Sort conversations by last message date
    conversationsArray.sort((a, b) => 
      new Date(b.lastMessage.created_at).getTime() - new Date(a.lastMessage.created_at).getTime()
    );

    setConversations(conversationsArray);

    // Select first conversation if none selected
    if (!selectedPhone && conversationsArray.length > 0) {
      setSelectedPhone(conversationsArray[0].phone);
    }
  }, [messages]);

  useEffect(() => {
    // Scroll to bottom when messages change or conversation is selected
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedPhone, messages]);

  async function fetchInstance() {
    try {
      const { data, error } = await supabase
        .from('instances')
        .select('id, name')
        .eq('id', instanceId)
        .single();

      if (error) throw error;
      setInstance(data);
    } catch (err) {
      console.error('Error fetching instance:', err);
      setError('Error al cargar los datos del canal');
    }
  }

  async function fetchMessages() {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('instance_id', instanceId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Error al cargar los mensajes');
      setLoading(false);
    }
  }

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString();
    }
  }

  function getStatusIcon(status: string) {
    switch (status) {
      case 'read':
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'delivered':
        return <CheckCircle className="h-4 w-4 text-gray-500" />;
      case 'sent':
        return <Clock className="h-4 w-4 text-gray-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  }

  const filteredConversations = conversations.filter(conv =>
    conv.phone.includes(searchTerm) ||
    conv.messages.some(msg => msg.content.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedConversation = conversations.find(conv => conv.phone === selectedPhone);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="h-16 bg-[#075E54] flex items-center px-4">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-white"
        >
          <ArrowLeft className="h-6 w-6 mr-2" />
          <span className="font-medium">{instance?.name}</span>
        </button>
      </div>

      <div className="flex h-[calc(100vh-4rem)]">
        {/* Conversations Sidebar */}
        <div className="w-96 bg-white border-r flex flex-col">
          <div className="p-2 border-b">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar conversación..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500"
              />
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                Cargando conversaciones...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No hay conversaciones
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.phone}
                  onClick={() => setSelectedPhone(conv.phone)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${
                    selectedPhone === conv.phone ? 'bg-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center">
                    <div className="h-12 w-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium">
                        {conv.phone.slice(-2)}
                      </span>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{conv.phone}</span>
                        <span className="text-sm text-gray-500">
                          {formatDate(conv.lastMessage.wa_timestamp || conv.lastMessage.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <MessageSquare className="h-4 w-4 mr-1" />
                        <span className="truncate">{conv.lastMessage.content}</span>
                        {conv.unreadCount > 0 && (
                          <span className="ml-2 bg-green-500 text-white rounded-full px-2 py-0.5 text-xs">
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 bg-[#E5DDD5] flex flex-col">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="bg-[#F0F2F5] px-4 py-2 flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {selectedConversation.phone.slice(-2)}
                    </span>
                  </div>
                  <div className="ml-3">
                    <div className="font-medium">{selectedConversation.phone}</div>
                    <div className="text-sm text-gray-500">
                      {selectedConversation.messages.length} mensajes
                    </div>
                  </div>
                </div>
                <button className="p-2 hover:bg-gray-200 rounded-full">
                  <MoreVertical className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4">
                {selectedConversation.messages.map((message, index) => (
                  <div
                    key={message.id}
                    className={`flex mb-4 ${
                      message.direction === 'outgoing' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg px-4 py-2 ${
                        message.direction === 'outgoing'
                          ? 'bg-[#DCF8C6]'
                          : 'bg-white'
                      }`}
                    >
                      <div className="text-sm">{message.content}</div>
                      <div className="flex items-center justify-end mt-1 space-x-1">
                        <span className="text-xs text-gray-500">
                          {formatDate(message.wa_timestamp || message.created_at)}
                        </span>
                        {message.direction === 'outgoing' && (
                          <div className="ml-1">{getStatusIcon(message.wa_status)}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              Selecciona una conversación
            </div>
          )}
        </div>
      </div>
    </div>
  );
}